// ============================================================
// EN <-> ID route pairs, one per content page. Used by
// LanguageSwitcher.tsx to jump to the equivalent page in the
// other language instead of always landing on the homepage.
//
// Zudoku 0.69.x has no i18n primitive, so the two languages are
// separate `/pages/{en,id}/**` trees with independently-slugged
// folders/files (e.g. "channels/overview" <-> "channel/ringkasan").
// There's no mechanical way to derive one path from the other, so
// this table is hand-maintained -- kept in its own module (rather
// than derived from zudoku.config.tsx's `navigation` tree at
// runtime) to avoid a circular import, since zudoku.config.tsx is
// the one that imports LanguageSwitcher.
//
// IMPORTANT: when adding/renaming/removing a page in
// zudoku.config.tsx's navigation for one language, mirror the
// change here. The pairs below match the sidebar's EN/ID category
// order 1:1 -- verified against zudoku.config.tsx on 2026-09-05.
// ============================================================

export const PAGE_TRANSLATIONS: ReadonlyArray<readonly [en: string, id: string]> = [
  ["/en/getting-started/introduction", "/id/memulai/pengenalan"],
  ["/en/getting-started/registration-and-login", "/id/memulai/registrasi-dan-login"],
  ["/en/getting-started/onboarding", "/id/memulai/onboarding"],
  ["/en/getting-started/quickstart", "/id/memulai/panduan-cepat"],
  ["/en/dashboard/overview", "/id/dashboard/ringkasan"],
  ["/en/channels/overview", "/id/channel/ringkasan"],
  ["/en/channels/whatsapp", "/id/channel/whatsapp"],
  ["/en/channels/whatsapp-business", "/id/channel/whatsapp-business"],
  ["/en/channels/instagram", "/id/channel/instagram"],
  ["/en/channels/facebook-messenger", "/id/channel/facebook-messenger"],
  ["/en/channels/telegram", "/id/channel/telegram"],
  ["/en/channels/web-chat", "/id/channel/web-chat"],
  ["/en/devices/device-management", "/id/perangkat/manajemen-perangkat"],
  ["/en/devices/add-new-device", "/id/perangkat/tambah-perangkat-baru"],
  ["/en/devices/monitor-and-reconnect", "/id/perangkat/monitor-dan-reconnect"],
  ["/en/ai-agent/overview", "/id/ai-agent/ringkasan"],
  ["/en/ai-agent/create-agent", "/id/ai-agent/buat-agent"],
  ["/en/ai-agent/ai-behaviors", "/id/ai-agent/perilaku-ai"],
  ["/en/ai-agent/handoff-prompts", "/id/ai-agent/handoff-prompt"],
  ["/en/ai-agent/knowledge-base", "/id/ai-agent/basis-pengetahuan"],
  ["/en/ai-agent/auto-follow-up", "/id/ai-agent/auto-follow-up"],
  ["/en/ai-agent/tools-configuration", "/id/ai-agent/konfigurasi-tools"],
  ["/en/ai-agent/testing", "/id/ai-agent/pengujian"],
  ["/en/workflow/overview", "/id/workflow/ringkasan"],
  ["/en/workflow/build-your-first-workflow", "/id/workflow/membuat-workflow-pertama"],
  ["/en/workflow/building-blocks", "/id/workflow/komponen-workflow"],
  ["/en/workflow/templates", "/id/workflow/template"],
  ["/en/workflow/activate-on-channel", "/id/workflow/mengaktifkan-di-channel"],
  ["/en/workflow/patterns", "/id/workflow/contoh-penggunaan"],
  ["/en/ai-tools/ecommerce-order", "/id/ai-tools/ecommerce-order"],
  ["/en/ai-tools/check-ongkir", "/id/ai-tools/cek-ongkir"],
  ["/en/ai-tools/web-search", "/id/ai-tools/web-search"],
  ["/en/human-agent/team-management", "/id/agen-manusia/manajemen-tim"],
  ["/en/human-agent/roles-and-permissions", "/id/agen-manusia/peran-dan-izin"],
  ["/en/chat/inbox-overview", "/id/chat/ringkasan-inbox"],
  ["/en/chat/conversation-management", "/id/chat/manajemen-percakapan"],
  ["/en/chat/chat-features", "/id/chat/fitur-chat"],
  ["/en/whatsapp-call/overview", "/id/panggilan-whatsapp/ringkasan"],
  ["/en/whatsapp-call/enable-calls", "/id/panggilan-whatsapp/mengaktifkan-panggilan"],
  ["/en/whatsapp-call/making-and-answering-calls", "/id/panggilan-whatsapp/melakukan-menerima-panggilan"],
  ["/en/whatsapp-call/call-details", "/id/panggilan-whatsapp/detail-panggilan"],
  ["/en/whatsapp-call/call-history", "/id/panggilan-whatsapp/riwayat-panggilan"],
  ["/en/whatsapp-call/requirements-and-limits", "/id/panggilan-whatsapp/ketentuan-batasan"],
  ["/en/contacts/contact-management", "/id/kontak/manajemen-kontak"],
  ["/en/contacts/import-contacts", "/id/kontak/impor-kontak"],
  ["/en/contacts/segments", "/id/kontak/segmen"],
  ["/en/broadcast/overview", "/id/broadcast/ringkasan"],
  ["/en/broadcast/create-broadcast", "/id/broadcast/buat-broadcast"],
  ["/en/broadcast/recipient-list", "/id/broadcast/daftar-penerima"],
  ["/en/broadcast/templates", "/id/broadcast/template"],
  ["/en/broadcast/analytics", "/id/broadcast/analitik"],
  ["/en/orders/overview", "/id/pesanan/ringkasan"],
  ["/en/orders/create-order", "/id/pesanan/buat-pesanan"],
  ["/en/orders/products", "/id/pesanan/produk"],
  ["/en/orders/shop-addresses", "/id/pesanan/alamat-toko"],
  ["/en/orders/order-management", "/id/pesanan/manajemen-pesanan"],
  ["/en/orders/payment", "/id/pesanan/pembayaran"],
  ["/en/orders/manual-payment", "/id/pesanan/pembayaran-manual"],
  ["/en/orders/invoices", "/id/pesanan/invoice"],
  ["/en/tickets/overview", "/id/tiket/ringkasan"],
  ["/en/tickets/ticket-board", "/id/tiket/papan-tiket"],
  ["/en/tickets/create-and-manage", "/id/tiket/buat-dan-kelola"],
  ["/en/tickets/ticket-templates", "/id/tiket/template-tiket"],
  ["/en/analytics/reports-overview", "/id/analitik/ringkasan-laporan"],
  ["/en/analytics/chat-analytics", "/id/analitik/analitik-chat"],
  ["/en/settings/profile", "/id/pengaturan/profil"],
  ["/en/settings/security", "/id/pengaturan/keamanan"],
  ["/en/settings/subscription-and-billing", "/id/pengaturan/langganan-dan-tagihan"],
  ["/en/affiliate/overview", "/id/afiliasi/ringkasan"],
  ["/en/affiliate/commission-and-payout", "/id/afiliasi/komisi-dan-pembayaran"],
];

const EN_TO_ID = new Map(PAGE_TRANSLATIONS.map(([en, id]) => [en, id]));
const ID_TO_EN = new Map(PAGE_TRANSLATIONS.map(([en, id]) => [id, en]));

/**
 * Returns the equivalent path in `targetLang` for a given pathname,
 * or undefined if this page has no known counterpart (e.g. EN-only
 * Developer Guides, or an unmapped/unrecognized path).
 */
export function getTranslatedPath(
  pathname: string,
  targetLang: "en" | "id",
): string | undefined {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return targetLang === "id" ? EN_TO_ID.get(normalized) : ID_TO_EN.get(normalized);
}
